import { useMemo, useState } from 'react';
import { subDays, subMonths } from 'date-fns';

import {
  getShortAgents,
  getLatencyThroughput,
  getLatencyThroughputSeries,
} from '../../../../../api';
import { useFetch } from '../../../../../hooks';
import { useAuth } from '../../../../../shared/User';
import { LatencyPeriod, intervalForPeriod } from '../const';

export type ThroughputForm = {
  agentId: string;
  conversationId: string;
  metricType: string;
  period: LatencyPeriod;
};

const defaultForm: ThroughputForm = {
  agentId: '',
  conversationId: '',
  metricType: 'llm',
  period: LatencyPeriod.TODAY,
};

export const useThroughput = () => {
  const { organization_id } = useAuth();
  const [form, setForm] = useState<ThroughputForm>(defaultForm);

  const interval = intervalForPeriod(form.period);

  const setMetricType = (metricType: string) =>
    setForm(prev => ({ ...prev, metricType }));

  // RFC3339 with an explicit UTC zone (the Go API parses time.RFC3339).
  const range = useMemo(() => {
    const now = new Date();
    const to = now.toISOString();
    let fromDate: Date;
    switch (form.period) {
      case LatencyPeriod.HALF_YEAR:
        fromDate = subMonths(now, 6);
        break;
      case LatencyPeriod.MONTH:
        fromDate = subDays(now, 30);
        break;
      case LatencyPeriod.WEEK:
        fromDate = subDays(now, 7);
        break;
      default:
        fromDate = subDays(now, 1);
    }
    return { from: fromDate.toISOString(), to };
  }, [form.period]);

  const { data: agentsData } = useFetch(async () => {
    const data = await getShortAgents();
    return (data ?? []).map(d => ({ value: d.id ?? '', label: d.title }));
  }, [organization_id]);

  const agentsOptions = useMemo(
    () => [{ value: '', label: 'All agents' }, ...(agentsData ?? [])],
    [agentsData]
  );

  const common = {
    from: range.from,
    to: range.to,
    agentId: form.agentId || undefined,
    conversationId: form.conversationId || undefined,
  };

  // The summary table is not affected by the chart's type filter.
  const tableDeps = [
    organization_id,
    form.period,
    form.agentId,
    form.conversationId,
  ];

  const throughput = useFetch(() => getLatencyThroughput(common), tableDeps);

  const throughputSeries = useFetch(
    () =>
      getLatencyThroughputSeries({
        ...common,
        metricType: form.metricType,
        interval,
      }),
    [...tableDeps, form.metricType]
  );

  return {
    form,
    setForm,
    setMetricType,
    agentsOptions,
    throughput,
    throughputSeries,
  };
};
